import React, { useCallback, useState } from 'react';
import { 
  getExpirationStatus
} from '../../utils/driverUtils';

const DOCUMENT_BADGE_CLASSES = {
  expired: 'bg-red-100 text-red-800 border-red-200',
  expiring: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  valid: 'bg-green-100 text-green-800 border-green-200',
  none: 'bg-gray-100 text-gray-600 border-gray-200'
};

const DriverList = ({ drivers = [], onViewDriver, onEditDriver, onAddDriver, onInviteDriver, onViewVehicle, onViewDocuments, onDeleteDriver }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);

  const handleVehicleNavigation = useCallback((vehicleId) => {
    if (!vehicleId || typeof onViewVehicle !== 'function') {
      return;
    }

    onViewVehicle(vehicleId);
  }, [onViewVehicle]);

  const vehicleNavigationEnabled = typeof onViewVehicle === 'function';

  const handleViewDocuments = (driverId) => {
    if (onViewDocuments) {
      onViewDocuments(driverId);
      return;
    }

    if (onViewDriver) {
      onViewDriver(driverId);
    }
  };

  const getDocumentSummary = (driver) => {
    const docStatus = getDriverDocumentStatus(driver);

    if (docStatus.hasExpired) {
      const countLabel = docStatus.expiredCount > 1 ? `${docStatus.expiredCount} Expired` : 'Expired';
      return {
        badgeClass: DOCUMENT_BADGE_CLASSES.expired,
        badgeLabel: countLabel,
        description: 'Click to review expired documents'
      };
    }

    if (docStatus.hasExpiring) {
      const countLabel = docStatus.expiringCount > 1 ? `${docStatus.expiringCount} Expiring Soon` : 'Expiring Soon';
      return {
        badgeClass: DOCUMENT_BADGE_CLASSES.expiring,
        badgeLabel: countLabel,
        description: 'Click to review upcoming expirations'
      };
    }

    if (driver.documentsStatus && driver.documentsStatus.length > 0) {
      return {
        badgeClass: DOCUMENT_BADGE_CLASSES.valid,
        badgeLabel: 'All Valid',
        description: 'Click to view documents'
      };
    }

    return {
      badgeClass: DOCUMENT_BADGE_CLASSES.none,
      badgeLabel: 'No Documents',
      description: 'Click to add documents'
    };
  };

  const buildDocumentSummaryBadges = (driver) => {
    const totalDocuments = Array.isArray(driver.documentsStatus)
      ? driver.documentsStatus.length
      : 0;

    const statusSummary = getDriverDocumentStatus(driver);

    if (totalDocuments === 0) {
      return [
        <span
          key="no-docs"
          className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600 border border-gray-200"
        >
          No documents on file
        </span>
      ];
    }

    const badges = [
      <span
        key="total"
        className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700 border border-blue-200"
      >
        Total Documents: {totalDocuments}
      </span>
    ];

    if (statusSummary.hasExpired) {
      badges.push(
        <span
          key="expired"
          className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 border border-red-200"
        >
          {statusSummary.expiredCount} Expired
        </span>
      );
    }

    if (statusSummary.hasExpiring) {
      badges.push(
        <span
          key="expiring"
          className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200"
        >
          {statusSummary.expiringCount} Expiring
        </span>
      );
    }

    const validCount = Math.max(
      0,
      totalDocuments - statusSummary.expiredCount - statusSummary.expiringCount
    );

    if (validCount > 0) {
      badges.push(
        <span
          key="valid"
          className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 border border-green-200"
        >
          {validCount} Valid
        </span>
      );
    }

    return badges;
  };

  const getDriverAssignment = (driver = {}) => {
    const assignedVehicles = Array.isArray(driver.assignedVehicles)
      ? driver.assignedVehicles
      : [];

    const vehicleId = driver.currentVehicleId || assignedVehicles[0] || null;
    const vehicleNumber = driver.currentVehicleNumber || driver.currentVehicle || vehicleId;

    if (!vehicleId && !vehicleNumber) {
      return null;
    }

    let assignedDateIso = null;
    let assignedDateLabel = null;
    if (driver.currentVehicleAssignedAt) {
      const assignedDate = new Date(driver.currentVehicleAssignedAt);
      if (!Number.isNaN(assignedDate.getTime())) {
        assignedDateIso = assignedDate.toISOString();
        assignedDateLabel = assignedDate.toLocaleDateString();
      }
    }

    return {
      vehicleId,
      vehicleNumber,
      assignedDate: assignedDateIso,
      assignedDateLabel
    };
  };

  const toggleRowExpansion = (driverId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(driverId)) {
      newExpandedRows.delete(driverId);
    } else {
      newExpandedRows.add(driverId);
    }
    setExpandedRows(newExpandedRows);
  };

  const filteredDrivers = drivers.filter(driver =>
    driver.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getDriverDocumentStatus = (driver) => {
    if (!driver.documentsStatus || driver.documentsStatus.length === 0) {
      return { hasExpired: false, hasExpiring: false, expiredCount: 0, expiringCount: 0 };
    }

    let hasExpired = false;
    let hasExpiring = false;
    let expiredCount = 0;
    let expiringCount = 0;

    driver.documentsStatus.forEach(docStatus => {
      const status = getExpirationStatus(docStatus.expirationDate, docStatus.type);
      if (status.urgency === 'high') {
        hasExpired = true;
        expiredCount++;
      } else if (status.urgency === 'medium') {
        hasExpiring = true;
        expiringCount++;
      }
    });

    return { hasExpired, hasExpiring, expiredCount, expiringCount };
  };

  const getDriverExpiringDocuments = (driver) => {
    if (!Array.isArray(driver.documentsStatus) || driver.documentsStatus.length === 0) {
      return [];
    }

    return driver.documentsStatus
      .map((docStatus) => {
        const statusDetails = getExpirationStatus(docStatus.expirationDate, docStatus.type);
        return {
          ...docStatus,
          statusDetails
        };
      })
      .filter(({ statusDetails }) => statusDetails && (statusDetails.urgency === 'medium' || statusDetails.urgency === 'high'))
      .sort((a, b) => {
        const aDate = new Date(a.expirationDate).getTime();
        const bDate = new Date(b.expirationDate).getTime();
        return aDate - bDate;
      });
  };


  const getLicenseStatusBadge = (status) => {
    return status === 'Active' 
      ? 'bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium'
      : 'bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium';
  };


  const handleEdit = (driverId) => {
    if (onEditDriver) {
      onEditDriver(driverId);
    }
  };

  const handleView = (driverId) => {
    if (onViewDriver) {
      onViewDriver(driverId);
    }
  };

  const handleDelete = (driver) => {
    setDeleteConfirmation(driver);
  };

  const confirmDelete = async () => {
    if (deleteConfirmation && onDeleteDriver) {
      await onDeleteDriver(deleteConfirmation.id);
      setDeleteConfirmation(null);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmation(null);
  };


  return (
    <>
      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={cancelDelete}></div>

            {/* Center modal */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                    Delete Driver
                  </h3>
                  <div className="mt-2 space-y-3">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete <span className="font-semibold text-gray-900">{deleteConfirmation.name}</span>? This action will terminate the driver.
                    </p>

                    {/* Document Warning */}
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-yellow-800">
                            Important: Driver Documents
                          </h4>
                          <div className="mt-2 text-sm text-yellow-700">
                            <p className="mb-2">
                              Driver documents will be <strong>permanently deleted in 30 days</strong> to give you time to download them.
                            </p>
                            <p className="text-xs text-yellow-600">
                              Note: You can manually delete documents permanently at any time from the driver's profile.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Delete Driver
                </button>
                <button
                  type="button"
                  onClick={cancelDelete}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    <div className="h-full flex flex-col">
      {/* Sticky Header */}
      <div className="sticky top-0 z-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                Driver Management
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Manage your fleet drivers and monitor their status
              </p>
            </div>
            <div className="flex space-x-3">
              {onInviteDriver && (
                <button
                  type="button"
                  onClick={onInviteDriver}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Invite Driver
                </button>
              )}
              <button
                onClick={onAddDriver}
                className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Driver
              </button>
            </div>
          </div>
          
          <div className="mt-4 space-y-4 sm:space-y-0 sm:flex sm:flex-row sm:gap-4">
            <div className="relative flex-1 sm:max-w-lg">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search drivers by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div className="flex items-center justify-center sm:justify-start space-x-2 text-center sm:text-left">
              <span className="text-sm text-gray-500">Total: {drivers.length} drivers</span>
              {searchTerm && (
                <span className="text-sm text-gray-500">• Found: {filteredDrivers.length}</span>
              )}
              {(() => {
                const expiredCount = filteredDrivers.filter(driver => getDriverDocumentStatus(driver).hasExpired).length;
                const expiringCount = filteredDrivers.filter(driver => getDriverDocumentStatus(driver).hasExpiring).length;
                
                if (expiredCount > 0 || expiringCount > 0) {
                  return (
                    <div className="flex items-center space-x-2">
                      {expiredCount > 0 && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 border border-red-200">
                          <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {expiredCount} with expired docs
                        </span>
                      )}
                      {expiringCount > 0 && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">
                          <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          {expiringCount} expiring soon
                        </span>
                      )}
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>
      </div>
      
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="hidden md:block">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center space-x-2">
                        <span>Driver</span>
                        <button
                          onClick={() => {
                            const allDriverIds = new Set(filteredDrivers.map(d => d.id));
                            const allExpanded = filteredDrivers.every(d => expandedRows.has(d.id));
                            if (allExpanded) {
                              setExpandedRows(new Set());
                            } else {
                              setExpandedRows(allDriverIds);
                            }
                          }}
                          className="text-gray-400 hover:text-gray-600 focus:outline-none"
                          title="Expand/Collapse all rows"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                          </svg>
                        </button>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vehicle
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Documents
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDrivers.map((driver) => {
                    const isExpanded = expandedRows.has(driver.id);
                    const assignment = getDriverAssignment(driver);
                    return (
                      <React.Fragment key={driver.id}>
                        {/* Main Row */}
                        <tr className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <button
                                onClick={() => toggleRowExpansion(driver.id)}
                                className="flex-shrink-0 mr-3 p-1 text-gray-400 hover:text-gray-600 focus:outline-none"
                              >
                                <svg 
                                  className={`h-4 w-4 transform transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                              <div className="flex-shrink-0 h-10 w-10 relative">
                                <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                  <span className="text-sm font-medium text-gray-700">{driver.name.split(' ').map(n => n[0]).join('')}</span>
                                </div>
                                {(() => {
                                  const docStatus = getDriverDocumentStatus(driver);
                                  if (docStatus.hasExpired) {
                                    return (
                                      <div className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full flex items-center justify-center">
                                        <svg className="h-2 w-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                      </div>
                                    );
                                  } else if (docStatus.hasExpiring) {
                                    return (
                                      <div className="absolute -top-1 -right-1 h-4 w-4 bg-yellow-500 rounded-full flex items-center justify-center">
                                        <svg className="h-2 w-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                              <div className="ml-4">
                                <div className="flex items-center space-x-2">
                                  <div className="text-sm font-medium text-gray-900">{driver.name}</div>
                                </div>
                                <div className="text-sm text-gray-500">ID: {driver.id}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col space-y-1">
                              <span className={getLicenseStatusBadge(driver.licenseStatus)}>
                                {driver.licenseStatus}
                              </span>
                              <span className="text-xs text-gray-500 capitalize">
                                {(driver.status || 'unknown').toString().replace(/_/g, ' ')}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {assignment ? (
                              vehicleNavigationEnabled ? (
                                <button
                                  type="button"
                                  onClick={() => handleVehicleNavigation(assignment.vehicleId)}
                                  className="flex items-center space-x-2 group focus:outline-none"
                                >
                                  <div className="flex-shrink-0">
                                    <div className="h-6 w-6 bg-blue-100 rounded-md flex items-center justify-center transition-colors group-hover:bg-blue-200">
                                      <svg className="h-3 w-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                                      </svg>
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-sm font-medium text-blue-700 group-hover:underline">
                                      {assignment.vehicleNumber}
                                    </div>
                                    <div className="text-xs text-gray-500">Active</div>
                                  </div>
                                </button>
                              ) : (
                                <div className="flex items-center space-x-2">
                                  <div className="flex-shrink-0">
                                    <div className="h-6 w-6 bg-blue-100 rounded-md flex items-center justify-center">
                                      <svg className="h-3 w-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                                      </svg>
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-sm font-medium text-blue-700">
                                      {assignment.vehicleNumber}
                                    </div>
                                    <div className="text-xs text-gray-500">Active</div>
                                  </div>
                                </div>
                              )
                            ) : (
                              <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                                Not Assigned
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {(() => {
                              const summary = getDocumentSummary(driver);
                              return (
                                <button
                                  type="button"
                                  onClick={() => handleViewDocuments(driver.id)}
                                  className="inline-flex items-center justify-between gap-2 w-44 px-3 py-1.5 rounded-md border border-transparent text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-300 transition-colors"
                                  title={summary.description}
                                  aria-label={`View documents for ${driver.name || 'driver'}`}
                                >
                                  <span className={`inline-flex items-center justify-center px-2 py-1 rounded-full border flex-shrink-0 ${summary.badgeClass}`}>
                                    {summary.badgeLabel}
                                  </span>
                                  <span className="flex-1 text-right">View</span>
                                </button>
                              );
                            })()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => handleView(driver.id)}
                                className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors duration-150"
                              >
                                <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                <span>View</span>
                              </button>
                              {driver.status !== 'terminated' && (
                                <>
                                  <button
                                    onClick={() => handleEdit(driver.id)}
                                    className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors duration-150"
                                  >
                                    <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    <span>Edit</span>
                                  </button>
                                  {onDeleteDriver && (
                                    <button
                                      onClick={() => handleDelete(driver)}
                                      className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors duration-150"
                                      title="Delete driver"
                                    >
                                      <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                      <span>Delete</span>
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                        
                        {/* Expanded Details Row */}
                        {isExpanded && (
                          <tr className="bg-gray-50">
                            <td colSpan="5" className="px-6 py-4">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* Medical & License Details */}
                                <div className="space-y-3">
                                  <h4 className="text-sm font-semibold text-gray-900">Certifications</h4>
                                  <div className="space-y-2">
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                      <span className="text-sm text-gray-600">Medical Cert:</span>
                                      <span className="text-sm text-gray-900">{new Date(driver.medicalCertExpiration).toLocaleDateString()}</span>
                                      {(() => {
                                        const medicalStatus = getExpirationStatus(driver.medicalCertExpiration, 'medical');
                                        return (
                                          <span className={`px-2 py-1 text-xs rounded-full font-medium ${medicalStatus.color}`}>
                                            {medicalStatus.status}
                                          </span>
                                        );
                                      })()}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                      <span className="text-sm text-gray-600">License:</span>
                                      <span className={getLicenseStatusBadge(driver.licenseStatus)}>
                                        {driver.licenseStatus}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* Vehicle Assignment Details */}
                                <div className="space-y-3">
                                  <h4 className="text-sm font-semibold text-gray-900">Vehicle Assignment</h4>
                                  <div className="space-y-2">
                                    {assignment ? (
                                      <>
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                          <span className="text-sm text-gray-600">Vehicle:</span>
                                          {vehicleNavigationEnabled ? (
                                            <button
                                              type="button"
                                              onClick={() => handleVehicleNavigation(assignment.vehicleId)}
                                              className="inline-flex px-2 py-1 text-sm font-medium rounded-full bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 focus:outline-none"
                                            >
                                              {assignment.vehicleNumber}
                                            </button>
                                          ) : (
                                            <span className="inline-flex px-2 py-1 text-sm font-medium rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                                              {assignment.vehicleNumber}
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                          <span className="text-sm text-gray-600">Since:</span>
                                          <span className="text-sm text-gray-900">{assignment.assignedDateLabel || '—'}</span>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                          <span className="text-sm text-gray-600">Status:</span>
                                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                            Active
                                          </span>
                                        </div>
                                      </>
                                    ) : (
                                      <div className="text-sm text-gray-500 text-center py-2">
                                        No vehicle assigned
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Documents Status */}
                                <div className="space-y-3">
                                  <h4 className="text-sm font-semibold text-gray-900">Documents</h4>
                                  <div className="flex flex-wrap gap-2">
                                    {buildDocumentSummaryBadges(driver)}
                                  </div>
                                  <div className="bg-white rounded-lg border p-3 space-y-2">
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Expiring Soon</span>
                                      <span className="text-xs text-gray-500">Next 60 days</span>
                                    </div>
                                    {(() => {
                                      const expiringDocs = getDriverExpiringDocuments(driver);
                                      if (!expiringDocs.length) {
                                        return (
                                          <p className="text-xs text-gray-500">No documents expiring soon.</p>
                                        );
                                      }
                                      return (
                                        <ul className="space-y-1">
                                          {expiringDocs.map((doc, idx) => {
                                            const status = doc.statusDetails;
                                            return (
                                              <li key={idx} className="text-xs text-gray-700 flex flex-wrap items-center gap-2">
                                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                                                  status.urgency === 'high' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                  {status.status}
                                                </span>
                                                <span className="font-medium">{doc.type}</span>
                                                <span className="text-gray-500">•</span>
                                                <span className="text-gray-500">Expires {new Date(doc.expirationDate).toLocaleDateString()}</span>
                                              </li>
                                            );
                                          })}
                                        </ul>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {filteredDrivers.length === 0 && (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center">
                          <svg className="h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <h3 className="text-lg font-medium text-gray-900 mb-1">No drivers found</h3>
                          <p className="text-sm text-gray-500">
                            {searchTerm ? `No drivers match "${searchTerm}"` : 'Get started by adding your first driver'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Mobile view */}
            <div className="md:hidden">
              <ul className="divide-y divide-gray-200 bg-slate-100">
                {filteredDrivers.map((driver) => {
                  const assignment = getDriverAssignment(driver);

                  return (
                    <li key={driver.id} className="px-4 py-4 bg-white rounded-xl shadow-sm border border-gray-200 mb-4 last:mb-0">
                      <div className="space-y-3">
                      {/* Driver Info Row */}
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0 relative">
                          <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-700">{driver.name.split(' ').map(n => n[0]).join('')}</span>
                          </div>
                          {(() => {
                            const docStatus = getDriverDocumentStatus(driver);
                            if (docStatus.hasExpired) {
                              return (
                                <div className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 rounded-full flex items-center justify-center">
                                  <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              );
                            } else if (docStatus.hasExpiring) {
                              return (
                                <div className="absolute -top-1 -right-1 h-5 w-5 bg-yellow-500 rounded-full flex items-center justify-center">
                                  <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="text-base font-medium text-gray-900 truncate">{driver.name}</p>
                        </div>
                          <p className="text-sm text-gray-500">ID: {driver.id}</p>
                        </div>
                      </div>
                      
                      {/* Status Badges Row */}
                      <div className="flex flex-wrap gap-2">
                        <span className={getLicenseStatusBadge(driver.licenseStatus)}>{driver.licenseStatus}</span>
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                          {(driver.status || 'unknown').toString().replace(/_/g, ' ')}
                        </span>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">Docs:</span>
                          {(() => {
                            const summary = getDocumentSummary(driver);
                              return (
                                <button
                                  type="button"
                                  onClick={() => handleViewDocuments(driver.id)}
                                  className="inline-flex w-36 justify-center focus:outline-none focus:ring-1 focus:ring-blue-300 rounded-full"
                                  title={summary.description}
                                  aria-label={`View documents for ${driver.name || 'driver'}`}
                                >
                                  <span className={`inline-flex items-center justify-center px-2 py-1 text-xs font-medium rounded-full border w-full ${summary.badgeClass}`}>
                                    {summary.badgeLabel}
                                  </span>
                                </button>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {buildDocumentSummaryBadges(driver)}
                      </div>

                      {(() => {
                        const expiringDocs = getDriverExpiringDocuments(driver);
                        if (!expiringDocs.length) {
                          return null;
                        }
                        return (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold text-yellow-800 uppercase tracking-wide">Documents expiring soon</span>
                              <span className="text-xs text-yellow-700">Next 60 days</span>
                            </div>
                            <ul className="space-y-1">
                              {expiringDocs.map((doc, idx) => (
                                <li key={idx} className="text-xs text-yellow-800 flex flex-wrap items-center gap-2">
                                  <span className="font-medium">{doc.type}</span>
                                  <span className="text-yellow-700">•</span>
                                  <span>Expires {new Date(doc.expirationDate).toLocaleDateString()}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      })()}

                      {/* Vehicle Assignment Row - Enhanced */}
                      <div className="bg-gray-50 rounded-lg p-3 border">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className="flex-shrink-0">
                              {assignment ? (
                                vehicleNavigationEnabled ? (
                                  <button
                                    type="button"
                                    onClick={() => handleVehicleNavigation(assignment.vehicleId)}
                                    className="h-6 w-6 bg-blue-100 rounded-md flex items-center justify-center hover:bg-blue-200 focus:outline-none transition-colors"
                                  >
                                    <svg className="h-3 w-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                                    </svg>
                                  </button>
                                ) : (
                                  <div className="h-6 w-6 bg-blue-100 rounded-md flex items-center justify-center">
                                    <svg className="h-3 w-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                                    </svg>
                                  </div>
                                )
                              ) : (
                                <div className="h-6 w-6 bg-gray-200 rounded-md flex items-center justify-center">
                                  <svg className="h-3 w-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            <span className="text-xs font-medium text-gray-700">Vehicle Assignment</span>
                          </div>
                        </div>
                        <div className="mt-2 ml-8">
                          {assignment ? (
                            <div className="space-y-2">
                              {vehicleNavigationEnabled ? (
                                <button
                                  type="button"
                                  onClick={() => handleVehicleNavigation(assignment.vehicleId)}
                                  className="flex items-center space-x-2 focus:outline-none group"
                                >
                                  <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-200 group-hover:bg-blue-100 group-hover:border-blue-300 transition-colors">
                                    {assignment.vehicleNumber}
                                  </span>
                                  <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                                    Active
                                  </span>
                                </button>
                              ) : (
                                <div className="flex items-center space-x-2">
                                  <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                                    {assignment.vehicleNumber}
                                  </span>
                                  <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                                    Active
                                  </span>
                                </div>
                              )}
                              <div className="flex items-center space-x-1 text-xs text-gray-500">
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span>Assigned since {assignment.assignedDateLabel || '—'}</span>
                              </div>
                            </div>
                          ) : (
                            <span className="inline-flex px-3 py-1 text-sm font-medium rounded-full bg-gray-100 text-gray-600">
                              Not Assigned
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Action Buttons Row */}
                      <div className="grid grid-cols-1 gap-2">
                        <button
                          onClick={() => handleView(driver.id)}
                          className="inline-flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                        >
                          <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View
                        </button>
                      </div>
                      
                      {/* Edit and Delete buttons - only for non-terminated drivers */}
                      {driver.status !== 'terminated' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(driver.id)}
                            className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                          >
                            <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit
                          </button>
                          {onDeleteDriver && (
                            <button
                              onClick={() => handleDelete(driver)}
                              className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 transition-colors"
                              title="Delete driver"
                            >
                              <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete
                            </button>
                          )}
                        </div>
                      )}
                      </div>
                    </li>
                  );
                })}
                {filteredDrivers.length === 0 && (
                  <li className="px-6 py-12">
                    <div className="text-center">
                      <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <h3 className="text-lg font-medium text-gray-900 mb-1">No drivers found</h3>
                      <p className="text-sm text-gray-500">
                        {searchTerm ? `No drivers match "${searchTerm}"` : 'Get started by adding your first driver'}
                      </p>
                    </div>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default DriverList;
